/**
 * Sorting shared by the Top merker and Top modeller grids (#586 AC2).
 *
 * Both cycle asc → desc → back to the server's order (count desc), the same
 * interaction KlasserTable uses. The text comparator carries a regression
 * history that is worth keeping in exactly one place — see compareText.
 *
 * No components here on purpose: the shared SortIcon lives in its own file so
 * this module stays pure logic and does not trip react-refresh.
 */

export type SortDir = 'asc' | 'desc'

/** Display/sort form of a text cell: trimmed, with blank meaning "no value". */
export const text = (v: string | null | undefined): string => (v ?? '').trim()

/**
 * Text cells: rows with no value sort last in both directions, so they never
 * displace data. Everything else uses the browser's nb-NO collation.
 *
 * Two blanks must compare equal. Returning a non-zero value for that pair
 * makes the comparator inconsistent, and V8's sort is then free to misplace
 * *unrelated* entries — a brand's model jumped to the top of the list (#481).
 *
 * Both sides are trimmed, because SVV names carry stray whitespace (" 37" is
 * a real model name). Sorting the untrimmed value puts the leading space
 * first while the cell renders trimmed, so the column looks mis-sorted for no
 * visible reason.
 */
export function compareText(
  a: string | null | undefined,
  b: string | null | undefined,
  dir: SortDir,
): number {
  const va = text(a)
  const vb = text(b)
  if (!va && !vb) return 0
  if (!va) return 1
  if (!vb) return -1
  return (dir === 'asc' ? 1 : -1) * va.localeCompare(vb, 'nb-NO')
}

export const compareNumber = (a: number, b: number, dir: SortDir): number =>
  (dir === 'asc' ? 1 : -1) * (a - b)

/** The asc → desc → unsorted cycle both grids share. */
export function nextSort<C extends string>(
  current: { col: C; dir: SortDir } | null,
  col: C,
): { col: C; dir: SortDir } | null {
  if (current?.col !== col) return { col, dir: 'asc' }
  return current.dir === 'asc' ? { col, dir: 'desc' } : null
}

export const thClass =
  'px-3 py-2 cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700 whitespace-nowrap'
